import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Stack, Alert, CircularProgress, IconButton, Tooltip, Chip, Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import { adminClient } from '../data/adminClient';
import TestSchoolChip from '../TestSchoolChip';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// Each school's Most Inspiring Dancer nomination (submitted from the teacher
// portal), plus which registered schools haven't submitted yet. Printing
// opens a PDF with one nomination per page, for reading at the banquet.
export default function InspiringDancersSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminClient.getInspiringDancers());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nominations = data?.nominations || [];
  const missing = data?.missing || [];
  // Test-school nominations (isTest) are shown but not counted, and left out
  // of "Print all" by the backend.
  const countedNominations = nominations.filter(n => !n.isTest);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          {data ? `${countedNominations.length} nomination${countedNominations.length === 1 ? '' : 's'} received` : ''}
          {data?.deadline ? ` · Due ${data.deadline}` : ''}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => adminClient.openInspiringDancerPdf()} disabled={countedNominations.length === 0}>
          Print all (one per page)
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {missing.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2, p: 2, borderColor: 'warning.main' }}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Not submitted yet ({missing.length})</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {missing.map(name => <Chip key={name} size="small" label={name} variant="outlined" />)}
              </Box>
            </Paper>
          )}

          {nominations.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No nominations have been submitted yet.</Typography>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {nominations.map(n => (
                <Paper key={n.schoolName} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography fontWeight={600}>{n.dancerName}{n.isTest && <TestSchoolChip />}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {n.schoolName} · Nominated by {n.teacherName}
                        {n.updatedAt ? ` · ${fmtDate(n.updatedAt)}` : ''}
                      </Typography>
                    </Box>
                    <Button size="small" startIcon={<PrintIcon fontSize="small" />} onClick={() => adminClient.openInspiringDancerPdf(n.schoolName)}>
                      Print
                    </Button>
                  </Box>
                  <Divider sx={{ my: 1.25 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{n.reason}</Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}
    </>
  );
}
